/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { ISbStoryData } from '@storyblok/react/rsc'

import { BlogCategoryStoryblok, BlogPostPageStoryblok } from '@/lib/storyblok/types/generated-types'
import { PaginatedBlogPosts } from '@/lib/storyblok/utils/api/blog-posts'
import { getExcerpt, getFeaturedImage } from '@/lib/storyblok/utils/blog-post-extractor'
import { BlogPost, Category } from '@/types/blog'

/**
 * Maps a Storyblok blog post to our domain blog post model.
 *
 * @param story - the Storyblok blog post
 */
const mapStoryblokToBlogPost = (story: ISbStoryData<BlogPostPageStoryblok>): BlogPost => {
  if (!story.content || !story.content.primary_category || typeof story.content.primary_category === 'string') {
    throw new Error('Invalid blog post')
  }

  return {
    id: story.uuid,
    title: story.content.title || story.name,
    slug: story.slug,
    description: getExcerpt(story.content),
    imageUrl: getFeaturedImage(story.content),
    publishedAt: story.first_published_at || story.published_at || '',
    category: story.content.primary_category
      ? {
          title: story.content.primary_category.name,
          slug: story.content.primary_category.slug,
        }
      : {
          title: 'Uncategorised',
          slug: 'uncategorised',
        },
  }
}

/**
 * Maps a Storyblok category to our domain category model.
 *
 * @param story - the Storyblok category
 */
const mapStoryblokToCategory = (story: ISbStoryData<BlogCategoryStoryblok>): Category => {
  return {
    id: story.uuid,
    title: story.content.name || story.name,
    slug: story.slug.replace('blog/category/', ''),
  }
}

/**
 * Maps paginated Storyblok blog posts to our domain model format.
 *
 * @param data - the paginated blog posts
 */
const mapPaginatedBlogPosts = (data: PaginatedBlogPosts) => {
  return {
    posts: data.posts.map(mapStoryblokToBlogPost),
    totalPages: data.totalPages,
    currentPage: data.currentPage,
    hasNextPage: data.hasNextPage,
    hasPreviousPage: data.hasPreviousPage,
  }
}

export { mapStoryblokToBlogPost, mapStoryblokToCategory, mapPaginatedBlogPosts }
